import { Dispatch, SetStateAction, useState } from 'react';
import { SalonActionPlan } from '../../../types/SalonActionPlan';
import { PLAN_TYPE } from '../constants';

type PlanTypeId =
  | (typeof PLAN_TYPE)['guestPlan']['id']
  | (typeof PLAN_TYPE)['teamPlan']['id']
  | (typeof PLAN_TYPE)['higherTicketsPlan']['id'];

type StatusObject = Record<
  PlanTypeId,
  { statusUpdate?: string | null; statusUpdateDate?: string | null }
>;

type StatusState = {
  statuses: StatusObject;
  actionedItems: { id: string; actioned: boolean }[];
};

export type InitiativesStatusApi = {
  statusState: StatusState | null;
  setStatusState: Dispatch<SetStateAction<StatusState>>;
  setData: (actionPlan: SalonActionPlan) => void;
  updateActionedItemById: (id: string) => void;
  getActionedItemValueById: (id: string) => boolean;
  getStatusObjectByPlanTypeId: (planTypeId: PlanTypeId) => StatusObject[PlanTypeId] | null;
  updateStatusObjectFieldByPlanTypeId: ({
    planTypeId,
    field,
    value,
  }: {
    planTypeId: PlanTypeId;
    field: keyof StatusObject[PlanTypeId];
    value: string;
  }) => void;
};

export const useInitiativesStatus = (): InitiativesStatusApi => {
  const [statusState, setStatusState] = useState<StatusState | null>(null);

  const setData = (actionPlan: SalonActionPlan) => {
    const actionedItems: StatusState['actionedItems'] = [];

    actionPlan.initiatives?.forEach((initiative) => {
      actionedItems.push({ id: initiative.id, actioned: initiative.crIsIncluded });
    });

    actionPlan.customResources?.forEach((resource) => {
      actionedItems.push({ id: resource.crUuid, actioned: resource.crIsIncluded });
    });

    const statuses = {
      [PLAN_TYPE.guestPlan.id]: {
        statusUpdate: actionPlan[PLAN_TYPE.guestPlan.id].statusUpdate,
        statusUpdateDate: actionPlan[PLAN_TYPE.guestPlan.id].statusUpdateDate,
      },
      [PLAN_TYPE.teamPlan.id]: {
        statusUpdate: actionPlan[PLAN_TYPE.teamPlan.id].statusUpdate,
        statusUpdateDate: actionPlan[PLAN_TYPE.teamPlan.id].statusUpdateDate,
      },
      [PLAN_TYPE.higherTicketsPlan.id]: {
        statusUpdate: actionPlan[PLAN_TYPE.higherTicketsPlan.id].statusUpdate,
        statusUpdateDate: actionPlan[PLAN_TYPE.higherTicketsPlan.id].statusUpdateDate,
      },
    };

    setStatusState({ statuses: statuses, actionedItems: actionedItems });
  };

  const getActionedItemValueById = (id: string) => {
    if (!statusState?.actionedItems) return false;

    const item = statusState.actionedItems.find((item) => item.id === id);
    return item ? item.actioned : false;
  };

  const updateActionedItemById = (id: string) => {
    setStatusState((prev) => {
      return {
        ...prev,
        actionedItems: prev.actionedItems.map((item) => {
          if (item.id === id) item.actioned = !item.actioned;

          return item;
        }),
      };
    });
  };

  const getStatusObjectByPlanTypeId = (planTypeId: PlanTypeId) => {
    if (!statusState?.statuses) {
      return null;
    }

    return statusState?.statuses[planTypeId];
  };

  const updateStatusObjectFieldByPlanTypeId = ({
    planTypeId,
    field,
    value,
  }: {
    planTypeId: PlanTypeId;
    field: keyof StatusObject[PlanTypeId];
    value: string;
  }) => {
    setStatusState((prev) => {
      return {
        ...prev,
        statuses: {
          ...prev.statuses,
          [planTypeId]: {
            ...prev.statuses[planTypeId],
            [field]: value,
          },
        },
      };
    });
  };

  return {
    statusState,
    setStatusState,
    setData,
    updateActionedItemById,
    getActionedItemValueById,
    getStatusObjectByPlanTypeId,
    updateStatusObjectFieldByPlanTypeId,
  };
};
